import React from 'react';
import { Edit2, Loader2 } from 'lucide-react';
import { t } from '../../i18n';

interface EditNicknameModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (newName: string) => void;
    isLoading: boolean;
    initialName: string;
    lang: string;
    error: string | null;
}

export const EditNicknameModal: React.FC<EditNicknameModalProps> = React.memo(({ 
    isOpen, 
    onClose, 
    onSave, 
    isLoading, 
    initialName, 
    lang,
    error 
}) => {
    const [tempName, setTempName] = React.useState(initialName);

    React.useEffect(() => {
        if (isOpen) setTempName(initialName);
    }, [isOpen, initialName]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md">
            <div className="bg-white w-full max-w-sm rounded-[40px] p-10 shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col">
                <h3 className="text-xl font-black text-slate-900 mb-2 uppercase tracking-tight text-center">{t(lang as any, 'editNickname')}</h3>
                
                <input 
                    type="text" 
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-5 py-4 text-lg font-black text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:bg-white transition-all mb-4 text-center"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    maxLength={20}
                    autoFocus
                />
                {error && <p className="text-[10px] font-black text-rose-500 mb-6 uppercase text-center tracking-widest">{error}</p>}
                
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-[0.2em] md:hover:text-slate-900 transition-colors">{t(lang as any, 'cancel')}</button>
                    <button 
                        onClick={() => onSave(tempName)} 
                        disabled={isLoading} 
                        className="flex-[2] bg-slate-900 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl active:scale-95 disabled:opacity-50"
                    >
                        {isLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : t(lang as any, 'saveChanges')}
                    </button>
                </div>
            </div>
        </div>
    );
});

EditNicknameModal.displayName = 'EditNicknameModal';
